import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { Flex, Text } from '@resideo/blueprint-react';
import footerLegalLinks from 'config/footer';

const FooterOuter = styled.footer`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 100%;
`;

const DrTextBox = styled(Text)`
  white-space: nowrap;
`;

interface Link {
  externalLink?: boolean;
}

/*
  for the styled component definition below, theme.fontSizes.xSmall
  is the same as 12px or 0.75rem.
  as defined in themes/base.ts, here are the size equivalents for the named values
  xxSmall  = 0.625rem (10px)
  xSmall   = 0.75rem  (12px)
  small    = 0.875rem (14px)
  medium   = 1rem     (16px)
  large    = 1.25rem  (20px)
  xLarge   = 1.5rem   (24px)
  xxLarge  = 2rem     (32px)
  xxxLarge = 2.5rem   (40px)
*/
const Link = styled.a<Link>`
  color: #888888;
  font-family: Roboto;
  font-size: ${({ theme }) => theme.fontSizes.xSmall};
  font-weight: 400;
  text-transform: uppercase; /* show links in all CAPS */
  text-decoration: none; /* do not underline links */
  svg {
    margin-left: ${({ theme, externalLink }) =>
      externalLink ? `${theme.space.small}` : '0'};
  }
`;

const DRGlobalTechLink = styled.a<Link>`
  color: #166a96;
  font-family: Roboto;
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: bold;
  text-transform: uppercase; /* show links in all CAPS */
  text-decoration: none; /* do not underline links */
  svg {
    margin-left: ${({ theme, externalLink }) =>
      externalLink ? `${theme.space.small}` : '0'};
  }
`;

const BottomLinks = styled.div`
  word-break: break-all;
  align-items: center;
  color: #888888;
  margin-bottom: ${({ theme }) => theme.space.large};
  margin-top: ${({ theme }) => theme.space.medium};
  padding-left: ${({ theme }) => theme.space.medium};
  padding-right: ${({ theme }) => theme.space.medium};
  text-align: center;
  font-weight: 500;
  a {
    word-break: break-all;
    color: ${({ theme }) => theme.colors.secondaryLink};
    font-size: ${({ theme }) => theme.fontSizes.xSmall};
    font-weight: 500;
    &:after {
      word-break: break-all;
      content: '|';
      padding: 0 ${({ theme }) => theme.space.small};
    }
    &:last-of-type:after {
      word-break: break-all;
      content: '';
    }
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.large}) {
    display: block;
    word-break: break-all;
  }
`;

const TrademarkSection = styled.p`
  word-break: break-all;
  align-items: center;
  color: ${({ theme }) => theme.colors.textHeading};
  font-size: 1rem;
  margin-top: ${({ theme }) => theme.space.large};
  padding: 0 ${({ theme }) => theme.space.small};
  font-family: Roboto;
  font-weight: normal;
  text-align: center;
  width: 65%;
`;

export const Footer = (): JSX.Element => {
  const { t } = useTranslation();
  const { pathname: path } = useLocation();

  return (
    <FooterOuter data-test-footer>
      {path.includes('cart') ? (
        <>
          <Flex paddingTop='large' justifyContent='center' textAlign='center'>
            <Text maxWidth='50rem' fontSize='small' lineHeight='1.313rem'>
              {t('cart.cartFooterInformation')}
            </Text>
          </Flex>
          <Flex paddingTop='0' justifyContent='center'>
            <Text
              maxWidth='50rem'
              fontSize='small'
              display='flex'
              lineHeight='1.313rem'>
              <a
                style={{
                  fontWeight: 'bold',
                  textDecoration: 'none',
                }}
                target='blank'
                href='https://store.digitalriver.com/store/defaults/en_US/DisplayDRAboutDigitalRiverPage/eCommerceProvider.DR+globalTech+Inc'>
                <DrTextBox color='primary' display='flex' lineHeight='1.313rem'>
                  {' '}
                  {t('cart.drGlobalTech')}&nbsp;
                </DrTextBox>
              </a>{' '}
              {t('cart.drGlobalTechText')}
            </Text>
          </Flex>
          <BottomLinks data-test-secondary-footer-nav-links>
            {footerLegalLinks.HAB.map(function(link, i): JSX.Element {
              return (
                <Link
                  href={link.linkPath}
                  id={link.teconsent ? 'teconsent' : ''}
                  target={!link.teconsent ? '_blank' : '_self'}
                  key={i}>
                  {t(link.textKey)}
                </Link>
              );
            })}
            <div id='teconsent'></div>
          </BottomLinks>
        </>
      ) : (
        <>
          <TrademarkSection data-test-footer-copyright>
            {t('footer.copyright')}{' '}
            <DRGlobalTechLink href='https://store.digitalriver.com/store/defaults/en_US/DisplayDRAboutDigitalRiverPage/eCommerceProvider.DR+globalTech+Inc.'>
              Dr GlobalTech, Inc.
            </DRGlobalTechLink>
            {t('footer.copyright2')}
          </TrademarkSection>
          <BottomLinks data-test-secondary-footer-nav-links>
            {footerLegalLinks.HAB.map(function(link, i): JSX.Element {
              return (
                <Link
                  href={link.linkPath}
                  id={link.teconsent ? 'teconsent' : ''}
                  target={!link.teconsent ? '_blank' : '_self'}
                  key={i}>
                  {t(link.textKey)}
                </Link>
              );
            })}
            <div id='teconsent'></div>
          </BottomLinks>
        </>
      )}
    </FooterOuter>
  );
};

export default Footer;
